import api, { http } from './api';

// ─────────────────────────────────────────────────────────────
// تأمین‌کنندگان (پخش‌کننده/تولیدکننده) و فاکتورهای خرید
// Backend: /admin/suppliers , /admin/purchase-invoices
// Prices: *Rial fields are Rial, *Toman fields are Toman.
// ─────────────────────────────────────────────────────────────

export type SupplierType = 'distributor' | 'manufacturer' | 'other';

export interface Supplier {
  _id: string;
  name: string;
  slug?: string;
  type: SupplierType;
  phone?: string;
  email?: string;
  address?: string;
  nationalId?: string;
  economicCode?: string;
  website?: string;
  notes?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  // list aggregates
  invoiceCount?: number;
  totalQuantity?: number;
  totalCostToman?: number;
  totalPaidRial?: number;
  totalSaleToman?: number;
  lastInvoiceDateJalali?: string | null;
}

export type SupplierInput = Partial<Omit<Supplier, '_id' | 'invoiceCount' | 'totalQuantity' | 'totalCostToman' | 'totalPaidRial' | 'totalSaleToman' | 'lastInvoiceDateJalali' | 'createdAt' | 'updatedAt'>> & {
  name: string;
};

export interface InvoiceLineProduct {
  _id: string;
  name: string;
  slug?: string;
  thumbnail?: string;
  stock?: number;
}

export interface PurchaseInvoiceLine {
  _id?: string;
  product?: InvoiceLineProduct | string | null;
  productCode?: string;
  name: string;
  brand?: string;
  quantity: number;
  unitBasePriceRial?: number;
  unitCostToman: number;
  unitSaleToman: number;
  note?: string;
}

export interface PurchaseInvoiceTotals {
  quantity: number;
  baseToman: number;
  costToman: number;
  saleToman: number;
}

export interface PurchaseInvoice {
  _id: string;
  invoiceNumber: string;
  supplier?: Pick<Supplier, '_id' | 'name' | 'slug' | 'type'> | string | null;
  store?: { _id: string; name?: string; storeName?: string; slug?: string } | string | null;
  invoiceDateJalali?: string;
  invoiceDate?: string;
  discountDescription?: string;
  paidAmountRial?: number;
  /** absent in list responses (see lineCount) */
  lines?: PurchaseInvoiceLine[];
  totals?: PurchaseInvoiceTotals;
  source?: string;
  notes?: string;
  createdAt?: string;
  /** list endpoint omits `lines` and sends these instead */
  lineCount?: number;
  brands?: string[];
}

export interface PurchaseInvoiceInput {
  invoiceNumber: string;
  supplier: string;
  store?: string;
  /** required by the backend: jYYYY/jMM/jDD — invoiceDate is derived server-side */
  invoiceDateJalali: string;
  discountDescription?: string;
  paidAmountRial?: number;
  notes?: string;
  lines: Array<Omit<PurchaseInvoiceLine, 'product'> & { product?: string }>;
}

export interface PurchaseSummary {
  invoiceCount: number;
  supplierCount?: number;
  totalQuantity: number;
  totalCostToman: number;
  totalSaleToman: number;
  potentialProfitToman: number;
  totalPaidRial?: number;
  lineCount?: number;
  byBrand: BrandSummaryRow[];
  bySupplier: SupplierSummaryRow[];
}

export interface BrandSummaryRow {
  brand: string;
  quantity: number;
  baseToman: number;
  costToman: number;
  saleToman: number;
  productCount: number;
  invoiceCount: number;
  supplierCount: number;
}

export interface SupplierSummaryRow {
  supplierId: string;
  name?: string;
  slug?: string;
  type?: SupplierType;
  invoiceCount: number;
  quantity: number;
  baseToman: number;
  costToman: number;
  saleToman: number;
  paidRial: number;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface SupplierQuery {
  page?: number;
  limit?: number;
  search?: string;
  type?: SupplierType | '';
  isActive?: boolean;
}

export interface InvoiceQuery {
  page?: number;
  limit?: number;
  search?: string;
  supplier?: string;
  /** Jalali jYYYY/jMM/jDD or ISO date (inclusive; backend handles both) */
  from?: string;
  /** Jalali jYYYY/jMM/jDD or ISO date (inclusive, end of day for Jalali) */
  to?: string;
}

const SUPPLIERS = '/admin/suppliers';
const INVOICES = '/admin/purchase-invoices';

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** api.get may already have unwrapped `{status,data}`; accept every shape. */
const unwrap = (raw: any): any => {
  if (raw && typeof raw === 'object' && 'status' in raw && 'data' in raw) return raw.data;
  return raw;
};

const toPaged = <T>(raw: any, keys: string[], fallbackLimit: number): Paged<T> => {
  const d = unwrap(raw);
  let items: T[] = [];
  if (Array.isArray(d)) items = d;
  else if (d && typeof d === 'object') {
    for (const k of [...keys, 'items', 'docs', 'results', 'data']) {
      if (Array.isArray(d[k])) {
        items = d[k];
        break;
      }
    }
  }
  const p = raw?.pagination || (d && !Array.isArray(d) && (d.pagination || d.meta)) || {};
  const total = num(p.total ?? p.totalItems ?? d?.total ?? items.length);
  const limit = num(p.limit ?? p.itemsPerPage ?? d?.limit) || fallbackLimit;
  const page = num(p.page ?? p.currentPage ?? d?.page) || 1;
  const pages = num(p.pages ?? p.totalPages) || Math.max(1, Math.ceil(total / limit));
  return { items, total, page, limit, pages };
};

/**
 * List endpoints return `{ status, data: [...], pagination }`. `api.get` strips the
 * envelope (and with it `pagination`), so lists go through the raw axios client.
 */
const getRaw = async (url: string, params: Record<string, any>): Promise<any> => {
  try {
    const res = await http.get<any>(url, { params });
    return res.data;
  } catch (err: any) {
    throw new Error(err?.response?.data?.message || err?.message || 'خطا در ارتباط با سرور');
  }
};

/** Backend nests list aggregates under `stats`; flatten them. */
const flattenSupplier = (s: any): Supplier => {
  if (!s || typeof s !== 'object') return s;
  const st = s.stats || {};
  return {
    ...s,
    invoiceCount: s.invoiceCount ?? st.invoiceCount ?? 0,
    totalQuantity: s.totalQuantity ?? st.totalQuantity ?? 0,
    totalCostToman: s.totalCostToman ?? st.totalCostToman ?? 0,
    totalSaleToman: s.totalSaleToman ?? st.totalSaleToman ?? 0,
    totalPaidRial: s.totalPaidRial ?? st.totalPaidRial ?? 0,
    lastInvoiceDateJalali: s.lastInvoiceDateJalali ?? st.lastInvoiceDateJalali ?? null,
  };
};

const pickOne = <T>(raw: any, key: string): T => {
  const d = unwrap(raw);
  return (d && typeof d === 'object' && d[key] ? d[key] : d) as T;
};

const clean = (params: Record<string, any>) => {
  const out: Record<string, any> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  });
  return out;
};

const supplierService = {
  async listSuppliers(q: SupplierQuery = {}): Promise<Paged<Supplier>> {
    const limit = q.limit ?? 50;
    const raw = await getRaw(SUPPLIERS, clean({ ...q, limit: Math.min(limit, 200) }));
    const paged = toPaged<any>(raw, ['suppliers'], limit);
    return { ...paged, items: paged.items.map(flattenSupplier) };
  },

  async getSupplier(id: string): Promise<Supplier> {
    const raw = await api.get<any>(`${SUPPLIERS}/${id}`);
    return flattenSupplier(pickOne<Supplier>(raw, 'supplier'));
  },

  async createSupplier(body: SupplierInput): Promise<Supplier> {
    const raw = await api.post<any>(SUPPLIERS, body);
    return pickOne<Supplier>(raw, 'supplier');
  },

  async updateSupplier(id: string, body: Partial<SupplierInput>): Promise<Supplier> {
    const raw = await api.put<any>(`${SUPPLIERS}/${id}`, body);
    return pickOne<Supplier>(raw, 'supplier');
  },

  async listInvoices(q: InvoiceQuery = {}): Promise<Paged<PurchaseInvoice>> {
    const limit = q.limit ?? 20;
    const raw = await getRaw(INVOICES, clean({ ...q, limit: Math.min(limit, 200) }));
    return toPaged<PurchaseInvoice>(raw, ['invoices', 'purchaseInvoices'], limit);
  },

  async getInvoice(id: string): Promise<PurchaseInvoice> {
    const raw = await api.get<any>(`${INVOICES}/${id}`);
    return pickOne<PurchaseInvoice>(raw, 'invoice');
  },

  async createInvoice(body: PurchaseInvoiceInput): Promise<PurchaseInvoice> {
    const raw = await api.post<any>(INVOICES, body);
    return pickOne<PurchaseInvoice>(raw, 'invoice');
  },

  async updateInvoice(id: string, body: Partial<PurchaseInvoiceInput>): Promise<PurchaseInvoice> {
    const raw = await api.put<any>(`${INVOICES}/${id}`, body);
    return pickOne<PurchaseInvoice>(raw, 'invoice');
  },

  async getSummary(q: Pick<InvoiceQuery, 'supplier' | 'from' | 'to'> = {}): Promise<PurchaseSummary> {
    const raw = await api.get<any>(`${INVOICES}/summary`, { params: clean(q) } as any);
    const body = pickOne<any>(raw, 'summary') || {};
    // backend shape: { overall: {...}, bySupplier: [...], byBrand: [...] }
    const d = body.overall || body;
    const cost = num(d.totalCostToman ?? d.costToman);
    const profit = d.potentialProfitToman ?? d.expectedProfitToman;
    const sale = num(d.totalSaleToman ?? d.saleToman);
    return {
      invoiceCount: num(d.invoiceCount ?? d.invoices),
      supplierCount: d.supplierCount !== undefined ? num(d.supplierCount) : undefined,
      totalQuantity: num(d.totalQuantity ?? d.quantity),
      totalCostToman: cost,
      totalSaleToman: sale,
      potentialProfitToman: profit !== undefined ? num(profit) : sale - cost,
      totalPaidRial: (d.totalPaidRial ?? d.paidRial) !== undefined ? num(d.totalPaidRial ?? d.paidRial) : undefined,
      lineCount: d.lineCount !== undefined ? num(d.lineCount) : undefined,
      byBrand: Array.isArray(body.byBrand) ? body.byBrand : [],
      bySupplier: Array.isArray(body.bySupplier) ? body.bySupplier : [],
    };
  },
};

/** Fallback totals when the backend didn't send `totals`. */
export const computeInvoiceTotals = (lines: PurchaseInvoiceLine[]): PurchaseInvoiceTotals =>
  lines.reduce<PurchaseInvoiceTotals>(
    (acc, l) => {
      const q = num(l.quantity);
      acc.quantity += q;
      acc.baseToman += (num(l.unitBasePriceRial) / 10) * q;
      acc.costToman += num(l.unitCostToman) * q;
      acc.saleToman += num(l.unitSaleToman) * q;
      return acc;
    },
    { quantity: 0, baseToman: 0, costToman: 0, saleToman: 0 },
  );

export const SUPPLIER_TYPE_LABELS: Record<SupplierType, string> = {
  distributor: 'پخش‌کننده',
  manufacturer: 'تولیدکننده',
  other: 'سایر',
};

export default supplierService;
